import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Zap, Target, Star, Crown, Rocket, Building2, Shield, MessageCircle, Clock, Users } from 'lucide-react';
import { cjmsApi } from '../api/cjmsApi';

interface ProgramSelectionProps {
    leadId: string;
    email: string;
    recommendedProgramId?: string;
    onComplete?: (selection: any) => void;
    onCancel?: () => void;
}

const PROGRAMS = [
    {
        id: 'free',
        name: 'Free 1-Pager',
        tagline: 'Get your first website free',
        price: 0,
        priceLabel: 'FREE',
        period: '',
        icon: Target,
        color: 'slate',
        features: [
            '1-page professional website',
            'Lifetime hosting included',
            'Mobile responsive design',
            'Basic SEO optimization',
            'Unlock by referring 5 friends',
        ],
        cta: 'Refer Friends to Unlock',
        popular: false,
    },
    {
        id: 'goal10',
        name: 'Goal-to-10',
        tagline: 'Find your first 10 customers',
        price: 197,
        priceLabel: 'R197',
        period: '/mo',
        icon: Rocket,
        color: 'blue',
        features: [
            '1-page professional website',
            'Business discovery tools',
            'Customer acquisition framework',
            'Sales pipeline basics',
            'WhatsApp support',
        ],
        cta: 'Start Your Sprint',
        popular: false,
    },
    {
        id: 'goal100',
        name: 'Goal-to-100',
        tagline: 'Scale to product-market fit',
        price: 497,
        priceLabel: 'R497',
        period: '/mo',
        icon: Star,
        color: 'indigo',
        features: [
            '3-page professional website',
            'Advanced sales automation',
            'Marketing playbooks',
            'Customer success tracking',
            'Priority WhatsApp support',
        ],
        cta: 'Scale Your Business',
        popular: true,
    },
    {
        id: 'goal1k',
        name: 'Goal-to-1K',
        tagline: 'Build your team & lead',
        price: 997,
        priceLabel: 'R997',
        period: '/mo',
        icon: Crown,
        color: 'purple',
        features: [
            '5-page professional website',
            'Team management tools',
            'Hiring & delegation framework',
            'Advanced analytics dashboard',
            'Dedicated support channel',
        ],
        cta: 'Go Professional',
        popular: false,
    },
    {
        id: 'goal10k',
        name: 'Goal-to-10K',
        tagline: 'Corporate conglomerate',
        price: 1497,
        priceLabel: 'R1,497',
        period: '/mo',
        icon: Building2,
        color: 'amber',
        features: [
            '8+ page professional website',
            'Multiple revenue streams',
            'Enterprise automation',
            'White-label options',
            'Executive coaching access',
        ],
        cta: 'Go Enterprise',
        popular: false,
    },
];

export const ProgramSelectionPage: React.FC<ProgramSelectionProps> = ({
    leadId,
    email,
    recommendedProgramId = 'goal10',
    onComplete,
    onCancel,
}) => {
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [step, setStep] = useState<'select' | 'confirm' | 'processing' | 'success' | 'error'>('select');
    const [errorMsg, setErrorMsg] = useState('');
    const [selectedProgram, setSelectedProgram] = useState<typeof PROGRAMS[0] | null>(null);

    const handleSelect = (program: typeof PROGRAMS[0]) => {
        setSelectedId(program.id);
        setSelectedProgram(program);
        setStep('confirm');
    };

    const handleConfirm = async () => {
        if (!selectedProgram) return;
        setStep('processing');
        try {
            if (selectedProgram.price === 0) {
                // Free tier — just record selection
                const result = await cjmsApi.chooseProgram({
                    leadId,
                    email,
                    programId: selectedProgram.id,
                    programName: selectedProgram.name,
                    price: 0,
                });
                if (result.success) {
                    setStep('success');
                    onComplete?.({ programId: selectedProgram.id, programName: selectedProgram.name, price: 0 });
                } else {
                    setStep('error');
                    setErrorMsg(result.error || 'Failed to record selection');
                }
            } else {
                // Paid tier — record selection + mock payment
                const selectResult = await cjmsApi.chooseProgram({
                    leadId,
                    email,
                    programId: selectedProgram.id,
                    programName: selectedProgram.name,
                    price: selectedProgram.price,
                });
                if (!selectResult.success) {
                    setStep('error');
                    setErrorMsg(selectResult.error || 'Failed to record selection');
                    return;
                }
                // Process mock payment
                const payResult = await cjmsApi.processPayment({
                    leadId,
                    email,
                    programId: selectedProgram.id,
                    amount: selectedProgram.price,
                });
                if (payResult.success) {
                    setStep('success');
                    onComplete?.({ programId: selectedProgram.id, programName: selectedProgram.name, price: selectedProgram.price, payment: payResult.payment });
                } else {
                    setStep('error');
                    setErrorMsg(payResult.error || 'Payment processing failed');
                }
            }
        } catch {
            setStep('error');
            setErrorMsg('Network error — please try again');
        }
    };

    if (step === 'confirm') {
        return (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-lg mx-auto p-6">
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
                    <h2 className="text-lg font-bold text-slate-800 mb-4">Confirm Your Selection</h2>
                    <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 mb-4">
                        <div className="flex items-center gap-3 mb-3">
                            {selectedProgram && React.createElement(selectedProgram.icon, { className: `w-6 h-6 text-blue-600` })}
                            <div>
                                <h3 className="text-sm font-bold text-slate-800">{selectedProgram?.name}</h3>
                                <p className="text-[10px] text-slate-500">{selectedProgram?.tagline}</p>
                            </div>
                        </div>
                        <div className="text-xl font-bold text-blue-600 mb-2">
                            {selectedProgram?.priceLabel}{selectedProgram?.period}
                        </div>
                        <ul className="space-y-1">
                            {selectedProgram?.features.map((f, i) => (
                                <li key={i} className="text-[10px] text-slate-600 flex items-center gap-1">
                                    <CheckCircle className="w-3 h-3 text-green-500" />
                                    {f}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-100 mb-4">
                        <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-amber-600" />
                            <p className="text-[10px] text-amber-700">
                                {selectedProgram?.price === 0
                                    ? 'Refer 5 friends to unlock your free website. No payment required.'
                                    : `You'll be charged R${selectedProgram?.price}/mo. Cancel anytime. All packages are self-paced — no coaching calls included.`}
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <button onClick={() => setStep('select')} className="flex-1 px-4 py-2 text-xs font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
                            Back
                        </button>
                        <button onClick={handleConfirm} className="flex-1 px-4 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-1">
                            <Zap className="w-3 h-3" />
                            {selectedProgram?.price === 0 ? 'Confirm Selection' : 'Proceed to Payment'}
                        </button>
                    </div>
                </div>
            </motion.div>
        );
    }

    if (step === 'processing') {
        return (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-lg mx-auto p-6 text-center">
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                    <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
                    <h2 className="text-lg font-bold text-slate-800 mb-2">Processing Your Selection</h2>
                    <p className="text-xs text-slate-500">Please wait while we set up your account...</p>
                </div>
            </motion.div>
        );
    }

    if (step === 'success') {
        return (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-lg mx-auto p-6 text-center">
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h2 className="text-lg font-bold text-slate-800 mb-2">Selection Confirmed!</h2>
                    <p className="text-xs text-slate-500 mb-4">
                        You've selected <strong>{selectedProgram?.name}</strong>.
                        {selectedProgram?.price === 0
                            ? ' Start referring friends to unlock your free website!'
                            : ' Welcome aboard! Your onboarding journey begins now.'}
                    </p>
                    <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 mb-4">
                        <div className="flex items-center gap-2 justify-center mb-2">
                            <MessageCircle className="w-4 h-4 text-blue-600" />
                            <p className="text-xs text-blue-700 font-medium">Questions Answered</p>
                        </div>
                        <p className="text-[10px] text-slate-600">
                            Need help getting started? WhatsApp us at +27 81 714 2228 or use the in-app chat. Our support team responds within 6-12 hours.
                        </p>
                    </div>
                    {onComplete && (
                        <button onClick={() => onComplete({ programId: selectedProgram?.id })} className="w-full px-4 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors">
                            Continue to Onboarding
                        </button>
                    )}
                </div>
            </motion.div>
        );
    }

    if (step === 'error') {
        return (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-lg mx-auto p-6 text-center">
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                    <p className="text-sm text-red-600 font-medium mb-4">{errorMsg}</p>
                    <button onClick={() => setStep('select')} className="px-4 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                        Try Again
                    </button>
                </div>
            </motion.div>
        );
    }

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-4xl mx-auto p-6">
            <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-slate-800 mb-2">Choose Your Program</h1>
                <p className="text-sm text-slate-500">Select the program that matches your business goals</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {PROGRAMS.map(program => {
                    const Icon = program.icon;
                    const isRecommended = program.id === recommendedProgramId;
                    return (
                        <motion.div
                            key={program.id}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: PROGRAMS.indexOf(program) * 0.08 }}
                            className={`relative p-5 rounded-2xl border-2 transition-all cursor-pointer ${
                                selectedId === program.id
                                    ? 'border-blue-500 bg-blue-50 shadow-md'
                                    : program.popular
                                        ? 'border-indigo-300 bg-indigo-50/30 hover:border-indigo-400'
                                        : 'border-slate-200 bg-white hover:border-blue-200 hover:shadow-sm'
                            }`}
                            onClick={() => handleSelect(program)}
                        >
                            {isRecommended && (
                                <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-full">
                                    Recommended
                                </div>
                            )}
                            {program.popular && (
                                <div className="absolute -top-2 right-4 bg-indigo-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-full">
                                    Most Popular
                                </div>
                            )}
                            <div className="flex items-center gap-2 mb-3">
                                <Icon className={`w-5 h-5 text-${program.color}-600`} />
                                <h3 className="text-sm font-bold text-slate-800">{program.name}</h3>
                            </div>
                            <p className="text-[10px] text-slate-500 mb-3">{program.tagline}</p>
                            <div className="mb-4">
                                <span className="text-2xl font-bold text-blue-600">{program.priceLabel}</span>
                                {program.period && <span className="text-xs text-slate-500">{program.period}</span>}
                            </div>
                            <ul className="space-y-1.5 mb-4">
                                {program.features.map((f, i) => (
                                    <li key={i} className="text-[10px] text-slate-600 flex items-center gap-1.5">
                                        <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                            <div className="p-2 bg-slate-50 rounded-lg">
                                <div className="flex items-center gap-1.5 justify-center">
                                    <Clock className="w-3 h-3 text-slate-400" />
                                    <span className="text-[9px] text-slate-500">Self-paced — no coaching calls included</span>
                                </div>
                            </div>
                        </motion.div>
                    );
                })}
            </div>

            <div className="flex items-center justify-center gap-2 p-4 bg-amber-50 rounded-xl border border-amber-100 mb-4">
                <Users className="w-4 h-4 text-amber-600" />
                <p className="text-[10px] text-amber-700">
                    All packages are self-paced. AI chatbot answers most questions. For human support, WhatsApp us at +27 81 714 2228 — responded within 6-12 hours.
                </p>
            </div>

            {onCancel && (
                <div className="text-center">
                    <button onClick={onCancel} className="text-xs text-slate-400 hover:text-slate-600 underline">
                        Back to dashboard
                    </button>
                </div>
            )}
        </motion.div>
    );
};
